import type { SojournDetailDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { formatDate } from '../../shared/format.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Badge,
  type BadgeTone,
  CollapsibleCard,
  EmptyState,
  LoadingBlock,
  TextInput,
} from '../../shared/ui/index.js';
import { buildItinerary, itineraryToCsv, type ItineraryEvent } from './sojournItinerary.js';
import { usePlan, useSetSojournDayNote } from './sojournHooks.js';

const EVENT_TONE: Record<ItineraryEvent['kind'], BadgeTone> = {
  arrival: 'positive',
  departure: 'neutral',
  move: 'accent',
};

/** `Virgo 01` → `virgo-01-itinerary.csv`, safe as a filename on every OS. */
function csvFileName(sojournName: string): string {
  const slug = sojournName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug === '' ? 'sojourn' : slug}-itinerary.csv`;
}

/**
 * The trip as a day-by-day sheet: who arrives, who leaves, who changes beds —
 * with a blank notes cell for whatever the plan itself does not capture, like
 * an arrival hour or "coming by train".
 */
export function SojournItinerary({ sojourn }: { sojourn: SojournDetailDto }): ReactElement {
  const t = useTranslation();
  const plan = usePlan(sojourn.id);
  const days = buildItinerary(sojourn, plan.data ?? null, {
    ...t.sojournItinerary,
    bedLabel: (type, option) => bedKindLabel(t, type, option),
  });

  return (
    <CollapsibleCard
      title={t.sojournItinerary.title}
      subtitle={t.sojournItinerary.subtitle(sojourn.hasPlan)}
      error={plan.error}
      actions={
        days.length > 0 ? (
          <a
            className="button button--secondary button--small"
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(
              itineraryToCsv(days, t.sojournItinerary.csvHeaders),
            )}`}
            download={csvFileName(sojourn.name)}
          >
            {t.sojournItinerary.exportCsv}
          </a>
        ) : null
      }
    >
      {plan.isPending ? <LoadingBlock label={t.sojournItinerary.loadingPlan} /> : null}

      {days.length === 0 ? (
        <EmptyState
          title={t.sojournItinerary.emptyTitle}
          description={t.sojournItinerary.emptyDescription}
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="itinerary-table">
            <thead>
              <tr>
                <th scope="col">{t.sojournItinerary.dateHeader}</th>
                <th scope="col">{t.sojournItinerary.eventsHeader}</th>
                <th scope="col">{t.sojournItinerary.notesHeader}</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => (
                <tr key={day.date}>
                  <td className="itinerary-table__date">{formatDate(day.date)}</td>
                  <td>
                    <div className="itinerary-table__events">
                      {day.events.map((event, index) => (
                        <Badge
                          key={`${event.kind}-${event.guestId}-${index}`}
                          tone={EVENT_TONE[event.kind]}
                        >
                          {event.description}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="itinerary-table__notes">
                    {/* Keyed by the saved note so a successful save remounts the
                          cell with the new canonical value, instead of an effect
                          reaching back to overwrite in-progress typing. */}
                    <NoteCell
                      key={day.note}
                      sojournId={sojourn.id}
                      date={day.date}
                      value={day.note}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
}

function NoteCell({
  sojournId,
  date,
  value,
}: {
  sojournId: string;
  date: string;
  value: string;
}): ReactElement {
  const t = useTranslation();
  const setNote = useSetSojournDayNote(sojournId);
  const [draft, setDraft] = useState(value);

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === value) {
      return;
    }
    setNote.mutate({ date, input: { note: trimmed } });
  };

  return (
    <TextInput
      aria-label={t.sojournItinerary.noteFor(formatDate(date))}
      value={draft}
      placeholder={t.sojournItinerary.notePlaceholder}
      disabled={setNote.isPending}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}
